import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { getEstateArchiveById, getProjectArchiveById } from '@/core/api/services/archives';
import {
  resourceIdAtom,
  resourceTypeAtom,
  scopeKeyAtom,
} from '@/features/document-archives/core/atoms/context';

type Order = 'asc' | 'desc';

export function useFolders(archiveId: string, _order: Order = 'desc') {
  const [resourceType] = useAtom(resourceTypeAtom);
  const [resourceId] = useAtom(resourceIdAtom);
  const [derivedScopeKey] = useAtom(scopeKeyAtom);

  const scopeKey =
    derivedScopeKey || (resourceType && resourceId ? `${resourceType}:${resourceId}` : '');

  const {
    data: archive,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    enabled: Boolean(resourceId) && Boolean(archiveId),
    queryKey: ['archive', scopeKey, archiveId],
    queryFn: async () => {
      if (resourceType === 'estate') {
        return getEstateArchiveById(resourceId as string, archiveId);
      }
      return getProjectArchiveById(resourceId as string, archiveId);
    },
  });

  const refresh = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    scopeKey,
    archive,
    loading,
    error,
    refresh,
  } as const;
}
